import { Collapse } from "antd"
import React, { useEffect } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { cn } from "../../../utils/cn.ts"
import { getTotalCounts } from "../helpers/utils.ts"
import { fetchPermissionsDisplay } from "../redux/permissions.thunks.ts.ts"
import type {
  OnBulkChange,
  OnFeaturesToggle,
  PermissionsFlat,
  PermissionsMode
} from "../types.ts"
import type { PermissionFeatures } from "../types/permissions.types.ts"
import { PermissionsCard } from "./PermissionsCard.tsx"
import { PermissionsCardLabel } from "./PermissionsCardLabel.tsx"

type PermissionsContentProps = {
  permissions: PermissionFeatures
  onBulkChange: OnBulkChange
  onFeaturesToggle?: OnFeaturesToggle
  disabled?: boolean
  isUpdating?: boolean
  containerClassName?: string
} & PermissionsMode

export function PermissionsContent({
  permissions,
  onBulkChange,
  onFeaturesToggle,
  isUpdating,
  disabled,
  containerClassName,
  ...rest
}: PermissionsContentProps) {
  const dispatch = useAppDispatch()
  const displayMap = useAppSelector(
    (state) => state.permissions?.permissionsDisplay ?? null
  )

  useEffect(() => {
    dispatch(fetchPermissionsDisplay())
  }, [])

  const modeProps: PermissionsMode = rest?.asForm
    ? { asForm: true }
    : { onItemChange: rest?.onItemChange }

  const collapseItems = permissions
    ? Object.entries(permissions as PermissionsFlat).map(([key, subKeys]) => {
        const parentKey = key as keyof PermissionFeatures
        const { enabled, total } = getTotalCounts(subKeys)

        return {
          key,
          className:
            "rounded-md border border-border shadow-sm overflow-hidden mb-2 h-fit",
          label: (
            <PermissionsCardLabel
              sectionKey={key}
              enabled={enabled}
              total={total}
            />
          ),
          // showArrow: false, // because some fields will not be registered in the form if it is collapsed
          children: (
            <PermissionsCard
              disabled={disabled}
              parentKey={parentKey}
              subKeys={subKeys}
              onBulkChange={onBulkChange}
              onFeaturesToggle={onFeaturesToggle}
              isUpdating={isUpdating}
              displayMap={displayMap}
              {...modeProps}
            />
          )
        }
      })
    : []

  return (
    <div
      className="transition-opacity duration-200"
      style={{ opacity: isUpdating ? 0.6 : 1 }}
    >
      <Collapse
        defaultActiveKey={Object.entries(permissions as PermissionsFlat).map(
          ([key]) => key
        )}
        items={collapseItems}
        className={cn(
          "bg-transparent border-none 2xl:grid 2xl:grid-cols-2 gap-3",
          containerClassName
        )}
      />
    </div>
  )
}
